import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/homeContent";
import { Plus, Pencil, Trash2, LogOut, Search, Star, ExternalLink, Loader2, Globe2, FileEdit, ListTree, Images } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/listings").then((res) => setListings(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const countries = [...new Set(listings.map((l) => l.country))].sort();
  const filtered = listings.filter(
    (l) => l.school_name.toLowerCase().includes(q.toLowerCase()) && (!country || l.country === country)
  );

  const del = async (id) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/listings/${id}`);
      toast.success("Listing deleted");
      setListings((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="BSD" className="h-9" />
            <span className="hidden sm:inline text-sm text-slate-400 border-l border-slate-200 pl-4">Admin Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-[#800020] inline-flex items-center gap-1.5" data-testid="admin-view-site">
              View Site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Link to="/admin/countries" className="text-sm text-slate-600 hover:text-[#800020] inline-flex items-center gap-1.5" data-testid="admin-nav-countries">
              <Globe2 className="h-3.5 w-3.5" /> Countries
            </Link>
            <Link to="/admin/menu" className="text-sm text-slate-600 hover:text-[#800020] inline-flex items-center gap-1.5" data-testid="admin-nav-menu">
              <ListTree className="h-3.5 w-3.5" /> Menu
            </Link>
            <Link to="/admin/media" className="text-sm text-slate-600 hover:text-[#800020] inline-flex items-center gap-1.5" data-testid="admin-nav-media">
              <Images className="h-3.5 w-3.5" /> Media
            </Link>
            <Link to="/admin/content" className="text-sm text-slate-600 hover:text-[#800020] inline-flex items-center gap-1.5" data-testid="admin-nav-content">
              <FileEdit className="h-3.5 w-3.5" /> Website Content
            </Link>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl text-slate-900">School Listings</h1>
            <p className="text-slate-500 text-sm mt-1">{listings.length} total · signed in as {user?.email}</p>
          </div>
          <Link to="/admin/listings/new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors" data-testid="admin-create-school-btn">
            <Plus className="h-4 w-4" /> New Listing
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search schools…" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:border-[#800020]" data-testid="admin-search-input" />
          </div>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="py-2.5 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-[#800020]" data-testid="admin-filter-country">
            <option value="">All Countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">No listings found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-5 py-3 font-medium">School</th>
                  <th className="px-5 py-3 font-medium">Country</th>
                  <th className="px-5 py-3 font-medium">Featured</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50" data-testid={`admin-row-${l.slug}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {l.image_url ? <img src={fileUrl(l.image_url)} alt="" className="h-10 w-14 object-cover rounded" /> : <div className="h-10 w-14 bg-slate-100 rounded" />}
                        <span className="font-medium text-slate-800">{l.school_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{l.country}</td>
                    <td className="px-5 py-3">{l.featured ? <Star className="h-4 w-4 text-[#C5A059] fill-[#C5A059]" /> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/school/${l.slug}`} target="_blank" className="p-2 rounded hover:bg-slate-100 text-slate-500" title="View"><ExternalLink className="h-4 w-4" /></Link>
                        <Link to={`/admin/listings/${l.id}/edit`} className="p-2 rounded hover:bg-slate-100 text-slate-600" data-testid={`admin-edit-${l.slug}`} title="Edit"><Pencil className="h-4 w-4" /></Link>
                        <button onClick={() => del(l.id)} disabled={deleting === l.id} className="p-2 rounded hover:bg-red-50 text-red-500" data-testid={`admin-delete-${l.slug}`} title="Delete">
                          {deleting === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
